import type { CapturePaypalPaymentReqBody, CountryKey, CreatePaymentIntentReqBody, CreatePayPalOrderReqBody, Order, SendContactMessageReqBody, SendResumeUrlReqBody, SendVisaReqBody, VisaForm, GetVisaDownloadUrlsQuery, TbankPaymentUrlReqBody, SendClientConfirmationReqBody, SerializedDb, PaymentData, UploadPhotoReqBody, EmailTemplate, SendOrderStatusReqBody, SendBillRequestReqBody, Coupon, GetFormsOptions, SendMobilePaymentAlertReqBody, GetSentEmailsOptions, ResendEmailReqBody, SendFixRequestEmailReqBody } from "~/types";
import type { NitroFetchRequest } from "nitropack";
import { buildHostUrlByBranch, getPaymentHandlerRoute, isAuthRoute, isCaptchaRoute, isPiiRoute } from "../helpers/routing";
import { AUTH_TOKEN_HEADER, CREATE_ORDER_MANUALLY_HEADER, PAYPAL_CAPTURE_HEADER, RECAPTCHA_TOKEN_HEADER } from "~/assets/constants";
import { getCaptchaAction, getRecaptchaToken } from "./recaptcha";
import { getAuthToken } from "./auth";
import { objectEntries } from "./general";
import { renamePii } from "./pii";

const call = $fetch.create({
  async onRequest({ request, options }) {
    const headerEntries = objectEntries(await getHeaders(request));
    for (const [key, value] of headerEntries) {
      options.headers.set(key, value);
    }
    if (isPiiRoute(request) && typeof options.body == 'object') {
      options.body = renamePii(options.body!);
    }
  },
  onResponseError(e) {
    console.error(e);
  }
});

export function getProducts(country: CountryKey) {
  return call(`/api/products/${country}`);
}

export function getAllProducts() {
  return call('/api/products');
}

export function getDiscount(code: string) {
  return call(`/api/discounts/${code}`);
}

export function saveForm(body: VisaForm) {
  return call('/api/save-form', { method: 'POST', body });
}

export function uploadPhoto(body: UploadPhotoReqBody) {
  return call('/api/upload-photo', { method: 'POST', body });
}

export function getResumeToken(id: VisaForm['id']) {
  return call(`/api/resume-token/${id}`);
}

export function sendResumeUrl(body: SendResumeUrlReqBody) {
  return call('/api/send-resume-url', { method: 'POST', body });
}

export function getResumeForm(token: string) {
  return call(`/api/resume-form/${token}`);
}

export function sendContactMessage(body: SendContactMessageReqBody) {
  return call('/api/send-contact-message', { method: 'POST', body });
}

export function createPaymentIntent(body: CreatePaymentIntentReqBody) {
  return call('/api/payment-intent', { method: 'POST', body });
}

export function cancelPaymentIntent(id: string) {
  return call('/api/payment-intent', { method: 'DELETE', body: { id } });
}

export function createPaypalOrder(body: CreatePayPalOrderReqBody) {
  return call('/api/create-paypal-order', { method: 'POST', body });
}

export function getTbankPaymentUrl(body: TbankPaymentUrlReqBody) {
  return call('/api/tbank-payment-url', {
    baseURL: buildHostUrlByBranch('co'),
    method: 'POST',
    body
  });
}

export function capturePaypalPayment(body: CapturePaypalPaymentReqBody) {
  return call(getPaymentHandlerRoute(), {
    body,
    method: 'POST',
    headers: { [PAYPAL_CAPTURE_HEADER]: '1' },
  });
}

export function createOrder(body: PaymentData) {
  return call(getPaymentHandlerRoute(), {
    body,
    method: 'POST',
    headers: { [CREATE_ORDER_MANUALLY_HEADER]: '1' },
  });
}

export function sendBillRequest(body: SendBillRequestReqBody) {
  return call('/api/send-bill-request', { method: 'POST', body });
}

export function sendMobilePaymentAlert(body: SendMobilePaymentAlertReqBody) {
  return call('/api/send-mobile-payment-alert', { method: 'POST', body });
}

export function sendOrderStatus(body: SendOrderStatusReqBody) {
  return call('/api/send-status', { method: 'POST', body });
}

export function getVisaDownloadUrls(query: GetVisaDownloadUrlsQuery) {
  return call('/api/visa-download-urls', { query });
}

export function getIp() {
  return call<string>('https://api.ipify.org');
}

// ADMIN ROUTES
export function previewEmail(body: { template: EmailTemplate, props: object }) {
  return call('/api/admin/email-preview', { method:'POST', body });
}

export function sendVisa(body: SendVisaReqBody) {
  return call('/api/admin/send-visa', { method:'POST', body });
}

export function getForms(query: GetFormsOptions) {
  return call('/api/admin/forms', { query });
}

export function sendForm(body: SerializedDb<VisaForm>) {
  return call('/api/admin/send-form', { method:'POST', body });
}

export function duplicateForm(id: VisaForm['id']) {
  return call('/api/admin/forms/duplicate', { method: 'POST', body: { id } });
}

export function deleteForm(id: VisaForm['id']) {
  return call(`/api/admin/forms/${id}`, { method: 'DELETE' });
}

export function getOrder(id: Order['id']) {
  return call(`/api/admin/orders/${id}`);
}

export function sendConfirmationToClient(body: SendClientConfirmationReqBody) {
  return call('/api/admin/send-client-confirmation', { method: 'POST', body });
}

export function createOrderRecord(body: Order) {
  return call('/api/admin/create-order-record', { method: 'POST', body });
}

export function deleteOrder(id: Order['id']) {
  return call(`/api/admin/orders/${id}`, { method: 'DELETE' });
}

export function getSentEmails(query: GetSentEmailsOptions) {
  return call('/api/admin/emails', { query });
}

export function getSentEmail(id: string) {
  return call<any>(`/api/admin/emails/${id}`);
}

export function resendEmail(body: ResendEmailReqBody) {
  return call('/api/admin/emails/resend', { method: 'POST', body });
}

export function getCoupons() {
  return call('/api/admin/coupons', { method: 'get' });
}

export function addCoupon(body: Coupon) {
  return call('/api/admin/coupons', { method: 'POST', body });
}

export function deleteCoupon(code: Coupon['code']) {
  return call(`/api/admin/coupons/${code}`, { method: 'DELETE' });
}

export function sendFixRequestEmail(body: SendFixRequestEmailReqBody) {
  return call('/api/admin/send-fix-email', { method: 'POST', body });
}

async function getHeaders(route: NitroFetchRequest) {
  const headers: Record<string, string> = {};
  if (isCaptchaRoute(route)) {
    headers[RECAPTCHA_TOKEN_HEADER] = await getCaptchaToken(route);
  }
  if (isAuthRoute(route)) {
    headers[AUTH_TOKEN_HEADER] = await getAuthToken();
  }
  return headers;
}

function getCaptchaToken(route: NitroFetchRequest) {
  const action = getCaptchaAction(route);
  return getRecaptchaToken(action);
}